import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getSocket, emitAck } from '../api/socket.js';
import { clearSavedPlayer } from '../hooks/useLocalToken.js';
import { AvatarBadge } from '../constants/avatars.jsx';
import { StarIcon } from '../components/Icons.jsx';
import PollQuestion from '../components/PollQuestion.jsx';
import McQuestion from '../components/McQuestion.jsx';
import CheckboxSimQuestion from '../components/CheckboxSimQuestion.jsx';
import OrderQuestion from '../components/OrderQuestion.jsx';
import MatchQuestion from '../components/MatchQuestion.jsx';
import ClassifyQuestion from '../components/ClassifyQuestion.jsx';
import ExprQuestion from '../components/ExprQuestion.jsx';
import NumericQuestion from '../components/NumericQuestion.jsx';
import StudentSlideView from '../components/StudentSlideView.jsx';
import { getLessonOrGeneric, getObjectiveForQuestion } from '../constants/lessons.js';
import '../styles/play.css';
import '../styles/themeTag.css';

export default function StudentPlay({ roomCode, token, initialPlayer }) {
  const [status, setStatus] = useState('lobby');
  const [question, setQuestion] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [answered, setAnswered] = useState(false);
  const [myResult, setMyResult] = useState(null);

  const lastQuestionId = useRef(null);

  const me = leaderboard.find((p) => p.id === initialPlayer.id) || {
    id: initialPlayer.id,
    nickname: initialPlayer.nickname,
    avatar: initialPlayer.avatar,
    score: initialPlayer.score || 0,
    stars: initialPlayer.stars || 0,
  };

  const applySync = useCallback((state) => {
    setStatus(state.status);
    setQuestion(state.currentQuestion);
    setLeaderboard(state.leaderboard || []);
    if (state.currentQuestion) {
      setAnswered(!!state.currentQuestion.alreadyAnswered);
      if (lastQuestionId.current !== state.currentQuestion.id) {
        lastQuestionId.current = state.currentQuestion.id;
        setMyResult(null);
      }
    }
  }, []);

  useEffect(() => {
    const socket = getSocket();

    const onSync = (state) => applySync(state);
    const onLeaderboard = ({ leaderboard: lb }) => setLeaderboard(lb);
    const onConnect = () => {
      emitAck('player:join', { roomCode, token });
    };

    // Out of the room means a new name on the way back in, so the old token
    // must not be offered as "welcome back".
    const onKicked = () => {
      clearSavedPlayer(roomCode);
      window.location.href = `/join/${roomCode}`;
    };
    // Teacher moved to a slide — if this phone is still showing an already
    // revealed question, drop back to the eyes-front view.
    const onSlide = () => {
      setQuestion((q) => (q && q.revealed ? null : q));
    };

    socket.on('state:sync', onSync);
    socket.on('leaderboard:update', onLeaderboard);
    socket.on('player:kicked', onKicked);
    socket.on('room:slide', onSlide);
    socket.on('connect', onConnect);
    return () => {
      socket.off('state:sync', onSync);
      socket.off('leaderboard:update', onLeaderboard);
      socket.off('player:kicked', onKicked);
      socket.off('room:slide', onSlide);
      socket.off('connect', onConnect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode, token, applySync]);

  const isLocked = answered || Boolean(question?.revealed);

  const submitAnswer = async (response) => {
    if (!question || answered || question.revealed) return;
    setAnswered(true);
    const res = await emitAck('answer:submit', {
      questionId: question.id,
      response,
      clientTimestamp: Date.now(),
    });
    if (res.ok) {
      setMyResult({ questionId: question.id, isCorrect: res.isCorrect, points: res.points });
    } else if (res.reason !== 'already-answered') {
      setAnswered(false);
    }
  };

  const revealedResult = question && question.revealed && myResult?.questionId === question.id ? myResult : null;
  // The leaderboard arrives with the reveal, so this is the rank after it.
  const myRankNow = leaderboard.findIndex((p) => p.id === initialPlayer.id) + 1;
  const lesson = getLessonOrGeneric(roomCode);
  const objective = question ? getObjectiveForQuestion(roomCode, question.orderIndex) : null;

  return (
    <div className={`page play-page ${question ? 'is-asking' : ''}`} data-theme={lesson.theme}>
      <header className="play-header row-between">
        <div className="row">
          <AvatarBadge avatar={me.avatar} size="sm" />
          <strong className="play-nickname">{me.nickname}</strong>
        </div>
        <div className="row play-stats">
          <span className="stat-pill star-pill row">
            <StarIcon size={13} />
            <span>{me.stars}</span>
          </span>
          <span className="stat-pill score-pill">
            <span className="num">{me.score}</span> คะแนน
          </span>
        </div>
      </header>

      <div className="play-lesson-bar row-between">
        <span className="play-lesson-unit">{lesson.unit}</span>
        {question ? (
          <span className="play-lesson-progress num">
            ข้อ {question.orderIndex + 1} / {question.totalQuestions}
          </span>
        ) : (
          <span className="play-lesson-progress text-muted">รหัส {roomCode}</span>
        )}
      </div>

      <main className="page-narrow stack">
        {!question && status === 'ended' && (() => {
          const myRank = leaderboard.findIndex((p) => p.id === initialPlayer.id) + 1;
          return (
            <div className="card text-center stack play-result-card">
              <span className="play-result-kicker">จบบทเรียนแล้ว</span>
              {myRank > 0 && (
                <div className="play-result-rank">
                  <span className="play-result-rank-num num">#{myRank}</span>
                  <span className="text-dim">จาก {leaderboard.length} คน</span>
                </div>
              )}
              <div className="play-result-stats row">
                <span className="stat-pill score-pill"><span className="num">{me.score}</span> คะแนน</span>
                <span className="stat-pill star-pill row"><StarIcon size={13} /><span>{me.stars} ดาวสะสม</span></span>
              </div>
              <p className="text-dim">ขอบคุณที่ตั้งใจเรียนวันนี้</p>
            </div>
          );
        })()}

        {!question && status !== 'ended' && (
          <StudentSlideView lessonUnit={lesson.unit} />
        )}

        {question && objective?.reflection && (
          <div className={`objective-chip play-objective ${objective.reflection ? 'is-reflection' : ''}`} key={question.id}>
            <span>{objective.label}</span>
          </div>
        )}

        {question && !question.revealed && !answered && question.type !== 'poll' && (
          <p className="play-speed-hint">⚡ ตอบถูก + ตอบเร็ว = ได้คะแนนมากกว่า</p>
        )}

        {question?.type === 'poll' && (
          <PollQuestion question={question} answered={isLocked} onSubmit={submitAnswer} />
        )}
        {question?.type === 'mc' && (
          <McQuestion question={question} answered={isLocked} onSubmit={submitAnswer} />
        )}
        {question?.type === 'checkbox_sim' && (
          <CheckboxSimQuestion question={question} answered={isLocked} onSubmit={submitAnswer} />
        )}
        {question?.type === 'order' && (
          <OrderQuestion question={question} answered={isLocked} onSubmit={submitAnswer} />
        )}
        {question?.type === 'match' && (
          <MatchQuestion question={question} answered={isLocked} onSubmit={submitAnswer} />
        )}
        {question?.type === 'classify' && (
          <ClassifyQuestion question={question} answered={isLocked} onSubmit={submitAnswer} look={lesson.theme} />
        )}
        {question?.type === 'expr' && (
          <ExprQuestion question={question} answered={isLocked} onSubmit={submitAnswer} />
        )}
        {question?.type === 'numeric' && (
          <NumericQuestion question={question} answered={isLocked} onSubmit={submitAnswer} />
        )}

        {/* Theme A plays the question like Kahoot: once an answer is in, the
            phone says so in big letters and sends eyes to the screen. */}
        {lesson.theme === 'tag' && question && answered && !question.revealed && (
          <div className="play-waiting" role="status">
            <span className="play-waiting-dots" aria-hidden="true"><i /><i /><i /></span>
            <strong>ส่งคำตอบแล้ว!</strong>
            <span>รอดูเฉลยบนจอด้านหน้า</span>
          </div>
        )}

        {revealedResult && revealedResult.isCorrect !== null && (
          <div
            className={`play-answer-result ${
              revealedResult.isCorrect ? 'is-correct' : revealedResult.points > 0 ? 'is-partial' : 'is-wrong'
            }`}
          >
            <strong>
              {revealedResult.isCorrect
                ? 'ตอบถูกต้อง'
                : revealedResult.points > 0
                ? 'ถูกบางส่วน'
                : 'ยังไม่ถูกต้อง'}
            </strong>
            {revealedResult.points > 0 && (
              <span className="play-answer-points num">+{revealedResult.points} คะแนน</span>
            )}
            {lesson.theme === 'tag' && myRankNow > 0 && (
              <span className="play-answer-rank">ตอนนี้อยู่อันดับที่ <b className="num">{myRankNow}</b></span>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
